/**
 * Die Bestelldaten oberhalb der Termine - Portierung von `index.html:8402-8436`, dem ersten Teil
 * des Abschnitts `main-data`: Lieferant, Lager, PI-Nummer, Rechnungsnummer, Liefertermin (Text).
 *
 * UMGEZOGEN (Auftrag „Reiter Spedition", 2026-08-20): Tracking-/Container-Nummer und
 * Speditionsportal (`index.html:8437-8446`) stehen seither im neuen Reiter „Spedition"
 * (`Drawer/ShipTab/Referenzen.tsx`, Abschnitt `ship-referenzen`) - wortgleich uebernommen, nur der
 * Ort hat gewechselt. Siehe `docs/superpowers/decisions/2026-08-20-reiter-spedition.md`.
 *
 * Die Felder liegen im Original alle in EINEM `.field-grid`, gemeinsam mit den Terminen
 * (`Termine.tsx`) und den Notizen (`BestellungTab.tsx`). Diese Komponente zeichnet deshalb ein
 * Fragment ohne eigene Huelle - jeder zusaetzliche Knoten waere ein Unterschied zur Baseline.
 *
 * ================= Zwei Helfer, die `lib/suppliers.ts` bewusst nicht traegt =================
 *
 * `supplierNames` (index.html:5002) und `supplierLogo` (:4855) stehen nicht in `lib/suppliers.ts` -
 * der Kopfkommentar dort nennt beide ausdruecklich als nicht portiert und `supplierNames` mit dem
 * Hinweis, dass es VIER Aufrufer hat. Hier stehen sie als lokale Portierung.
 *
 * RICHTIGGESTELLT (Nachtrag "Bestellfelder nach Anlage sperren", 2026-08-25): `supplierNames`
 * hatte seinen einzigen Aufrufer hier in der Lieferanten-`<select>`-Auswahl, die mit diesem Auftrag
 * durch ein schreibgeschuetztes `<input>` ersetzt wurde (Lieferant wird bei der Anlage festgelegt
 * und ist danach unveraenderlich). Dieser Reiter ist seither KEIN Aufrufer mehr - der einzige
 * verbliebene ist ein Test (`test/komponenten/api/bestellungen.test.ts`). Die Funktion bleibt
 * trotzdem hier UND exportiert stehen, siehe die Anmerkung beim Lieferanten-Feld unten.
 *
 * - `supplierNames` liest im Original `allSuppliers()` UND `DB.orders`; hier kommen beide als
 *   Argument. Rechenweg unveraendert (Set, dann `localeCompare(..., 'de')`).
 * - `supplierLogo` faellt fuer ein Logo, das kein `http(s):`/`data:`/`blob:` ist, im Original auf
 *   `fileUrl(logo)` zurueck - das haengt `?session=<token>` an (index.html:5330). `fileUrl` ist
 *   nicht portiert (die SPA authentifiziert ueber `Authorization: Bearer`, siehe
 *   `src/api/basis.ts`); der Pfad geht deshalb unveraendert durch. GEMELDET, nicht repariert:
 *   solange die Dateiablage der SPA nicht steht, ist das die einzige Form, die keinen Token
 *   erfindet. In der Baseline traegt kein Lieferant ein Logo - der Zweig wird dort nie genommen.
 */
import { useEffect, useRef, useState } from "react";
import { supplierBadgeUrl, supplierByName, allSuppliers } from "../../../lib/suppliers.ts";
import type { Order, Supplier } from "../../../lib/types.ts";
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import { SAMMELSENDUNG_PFLEGEHINWEIS, sperrendeSammelsendung } from "../sammelsendungSperre.ts";

/** `index.html:5002` - `allSuppliers()` und `DB.orders` sind hier Argumente. */
export function supplierNames(
  lieferanten: readonly Supplier[] | null | undefined,
  bestellungen: readonly Order[] | null | undefined,
): string[] {
  const names = new Set<string>();
  for (const s of allSuppliers(lieferanten)) {
    const n = ((s.name || "") as string).trim();
    if (n) names.add(n);
  }
  for (const o of bestellungen || []) {
    const n = ((o.supplier || "") as string).trim();
    if (n) names.add(n);
  }
  return [...names].sort((a, b) => a.localeCompare(b, "de"));
}

/** `index.html:4855` - `DB.suppliers` ist hier ein Argument, `fileUrl` entfaellt (Kopfkommentar). */
export function supplierLogo(
  name: unknown,
  lieferanten: readonly Supplier[] | null | undefined,
): string {
  const s = supplierByName(name, lieferanten);
  if (s && s.logo) {
    // Original: `/^(https?:|data:|blob:)/i.test(logo) ? logo : fileUrl(logo)`. Ohne `fileUrl`
    // liefern beide Zweige denselben Wert; die Verzweigung waere hier nur noch Attrappe und ist
    // deshalb weggelassen statt vorgetaeuscht. Siehe Kopfkommentar.
    return String(s.logo);
  }
  return supplierBadgeUrl(name || "Lieferant");
}

/** `index.html:4863` - als JSX statt als Zeichenkette. */
function LieferantenLogo({
  name,
  lieferanten,
  className = "supplier-logo",
}: {
  name: unknown;
  lieferanten: readonly Supplier[] | null | undefined;
  className?: string;
}) {
  return (
    <img
      className={className}
      src={supplierLogo(name, lieferanten)}
      alt={(name as string) || "Lieferant"}
      draggable="false"
    />
  );
}

export interface StammdatenProps {
  bestellung: Order;
  lieferanten: readonly Supplier[];
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * Auftrag C2b/generischer Kopf-Teilupdate-Weg: der Schreibweg des Bezugsauftrag-Felds - siehe
   * `MainTab/typen.ts`.
   *
   * NACHTRAG 3 (2026-08-25, G-1): dieser Rueckruf schrieb ueber denselben Kanal urspruenglich auch
   * das Lager-Auswahlfeld (`kLager`) - das ist seit diesem Nachtrag read-only (siehe Kopfkommentar
   * am Lager-Feld unten, Backend-Riegel `http/routes/jtlSchreiben.ts#alsTeilKopf`), `Stammdaten.tsx`
   * schreibt `kLager` darueber nicht mehr. `onLieferant` (Lieferanten-Auswahl) ist mit demselben
   * Nachtrag komplett aus dieser Aufrufform gestrichen, nicht nur stillgelegt.
   */
  onJtlKopfAendern?: (id: string, kopf: Record<string, unknown>) => void;
}

export function Stammdaten({
  bestellung: o,
  lieferanten,
  onAendern,
  onJtlKopfAendern,
}: StammdatenProps) {
  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });
  // Task 7: Rechnungsnummer ist ein geteiltes Kopffeld der Sammelsendung.
  const sammelsendungGesperrt = sperrendeSammelsendung(o) !== null;

  /**
   * Auftrag „Bezugsauftrag schreibbar machen ueber den generischen Weg" (2026-08-25): dasselbe
   * Muster wie `notizEntwurf`/`notizVerlassen` in `BestellungTab.tsx` (dortiger Kopfkommentar traegt
   * die volle Begruendung, hier nur die Kurzfassung) - dieses Feld schrieb bis hierher gar nicht
   * (`readOnly`), der GENERISCHE Kopf-Teilupdate-Weg (`onJtlKopfAendern`, `PATCH
   * /api/jtl/orders/:k`) kennt `cBezugsAuftragsNummer` (grosses N, `spLieferantenBestellungBearbeiten`,
   * siehe `prozeduren.ts:126`) aber bereits als Parameter - das Backend braucht dafuer keine einzige
   * neue Zeile.
   *
   * Der lokale Entwurf schreibt erst bei `onBlur`, nicht bei jedem Anschlag - aus demselben Grund wie
   * beim Notizfeld: das Ziel ist ein ERP-Beleg MIT optimistischer Sperre
   * (`bRowversion`/`kopfSchreibKette` in `App.tsx`), zwei Anschlaege vor der ersten Antwort duerften
   * nicht denselben `bRowversion`-Stand zweimal verschicken. Der Effekt unterscheidet Bestellungs-
   * wechsel (Entwurf IMMER auf den neuen Serverwert zuruecksetzen) von einer Aenderung DERSELBEN
   * Bestellung (nur uebernehmen, wenn kein abweichender, noch ungeschriebener Entwurf offen ist) -
   * exakt das Race, das `notizVerlassen` in `BestellungTab.tsx` mit `zuletztServerWertRef` schliesst.
   *
   * Leerung: ein geleertes Feld schreibt `""` (leerer Text), NICHT `null` - dieselbe Form, die
   * `notizVerlassen` fuer `cInternerKommentar` verschickt (der lokale Entwurf ist dort wie hier immer
   * eine Zeichenkette, nie `null`). Beide Werte sind laut `alsTeilKopf` (`jtlSchreiben.ts`) gueltig
   * ("Text, Zahl oder null"); ein expliziter Sonderfall fuer `null` beim Leeren waere eine zweite,
   * unbegruendete Abweichung vom Vorbild.
   */
  const [bezugsauftragEntwurf, setBezugsauftragEntwurf] = useState<string>(
    (o.bezugsAuftragsNummer as string) ?? "",
  );
  const vorigeBezugsauftragIdRef = useRef(id);
  const zuletztServerBezugsauftragRef = useRef<string>((o.bezugsAuftragsNummer as string) ?? "");
  useEffect(() => {
    const serverWert = (o.bezugsAuftragsNummer as string) ?? "";
    const bestellungsWechsel = vorigeBezugsauftragIdRef.current !== id;
    const bekannterServerWert = zuletztServerBezugsauftragRef.current;
    vorigeBezugsauftragIdRef.current = id;
    zuletztServerBezugsauftragRef.current = serverWert;
    if (bestellungsWechsel) {
      setBezugsauftragEntwurf(serverWert);
      return;
    }
    setBezugsauftragEntwurf((bisher) => (bisher === bekannterServerWert ? serverWert : bisher));
  }, [id, o.bezugsAuftragsNummer]);

  /** Siehe Kopfkommentar `bezugsauftragEntwurf` oben - schreibt nur, wenn sich der Entwurf vom JTL-Stand unterscheidet. */
  const bezugsauftragVerlassen = (): void => {
    const bisher = (o.bezugsAuftragsNummer as string) ?? "";
    if (bezugsauftragEntwurf === bisher) return;
    onJtlKopfAendern?.(id, { cBezugsAuftragsNummer: bezugsauftragEntwurf });
  };

  /**
   * Root-Cause-Nachbesserung (Task 1218081223201411, Bugreport "Feld Rechnungsnummer lässt sich
   * nicht bearbeiten", Bestellung BE-10360): PI-Nummer und Rechnungsnummer schrieben bis hierher bei
   * JEDEM Tastendruck sofort ueber `patch(...)` -> `onAendern` -> `App.tsx#drawerAendern` -> PATCH
   * `/api/orders/:id/extras` mit der zum Renderzeitpunkt bekannten `extrasRowVersion`. Zwei
   * Anschlaege vor der ersten Antwort schickten dieselbe, inzwischen veraltete `extrasRowVersion`
   * zweimal - der zweite PATCH lief deterministisch in einen Versionskonflikt (409,
   * `VersionConflictError`, `backend/src/domaene/extras.ts:99-117`), den `App.tsx#drawerAendern`s
   * `.catch` nur als generischen Toast meldet und NICHTS lokal uebernimmt. Fuer den Nutzer wirkte das
   * Feld dadurch praktisch unbearbeitbar (in der Praxis oft schon beim ERSTEN Tastendruck, sobald
   * die Bestellung zwischen Laden und Tippen anderweitig serverseitig aktualisiert wurde).
   *
   * Dasselbe Muster wie `bezugsauftragEntwurf` oben (dortiger Kopfkommentar traegt die volle
   * Begruendung des lokalen Entwurfszustands, hier nur die Kurzfassung): JEDES der beiden Felder
   * bekommt ein EIGENES `useState`/`useRef`-Paar, der lokale Entwurf haelt `onInput` waehrend des
   * Tippens nach, geschrieben wird erst bei `onBlur`.
   *
   * Leerung: ein geleertes Feld schreibt `""`, NICHT `null` - dieselbe Form wie beim Bezugsauftrag
   * (siehe dessen Kopfkommentar).
   *
   * NACHTRAG (2026-09-04, selber Tag, nach Ruecklauf auf demselben Task): der Schreibweg oben
   * ("PI-Nummer/Rechnungsnummer liegen nicht in JTL") stimmte fuer PI-Nummer, NICHT fuer
   * Rechnungsnummer - siehe deren eigenen Kopfkommentar an `invoiceNoEntwurf` unten. PI-Nummer bleibt
   * `patch(...)` -> `onAendern` (Hub-Overlay, `order_extras.pi_no`), Rechnungsnummer nicht mehr.
   */
  const [piNoEntwurf, setPiNoEntwurf] = useState<string>((o.piNo as string) ?? "");
  const vorigePiNoIdRef = useRef(id);
  const zuletztServerPiNoRef = useRef<string>((o.piNo as string) ?? "");
  useEffect(() => {
    const serverWert = (o.piNo as string) ?? "";
    const bestellungsWechsel = vorigePiNoIdRef.current !== id;
    const bekannterServerWert = zuletztServerPiNoRef.current;
    vorigePiNoIdRef.current = id;
    zuletztServerPiNoRef.current = serverWert;
    if (bestellungsWechsel) {
      setPiNoEntwurf(serverWert);
      return;
    }
    setPiNoEntwurf((bisher) => (bisher === bekannterServerWert ? serverWert : bisher));
  }, [id, o.piNo]);

  /** Siehe Kopfkommentar `piNoEntwurf` oben - schreibt nur, wenn sich der Entwurf vom Serverstand unterscheidet. */
  const piNoVerlassen = (): void => {
    const bisher = (o.piNo as string) ?? "";
    if (piNoEntwurf === bisher) return;
    patch("piNo", piNoEntwurf);
  };

  /**
   * Root-Cause-Nachbesserung Nummer 2 (Task 1218081223201411, selber Bugreport, Ruecklauf desselben
   * Tages: "Das eintragen ist moeglich, aber es speichert den Wert nicht"): der Blur-Fix oben behob
   * das 409-Rennen, aendert aber NICHTS daran, dass `o.invoiceNo` beim naechsten Laden verschwand.
   * Ursache ist der `patch(...)`-Schreibweg selbst, nicht mehr die 409-Kollision: `invoiceNo` ist
   * KEIN reines Hub-Feld wie `piNo`, sondern die Fremdbelegnummer aus JTL (`cFremdbelegnummer`,
   * `api/jtlBestellungen.ts#zusammenfuehren`, Kopfkommentar dort "WELCHE SEITE GEWINNT": bei jedem
   * Feld, das beide Seiten fuehren, gewinnt JTL). `zusammenfuehren` setzt `invoiceNo: jtl.
   * fremdbelegnummer ?? ""` NACH der `...hub`-Spreizung - ein per `patch("invoiceNo", ...)` in
   * `order_extras.invoice_no` geschriebener Wert wird beim naechsten Laden also unbedingt durch den
   * (meist leeren) JTL-Stand ersetzt. Der PATCH selbst lief fehlerfrei durch (kein Toast, kein
   * Konflikt) - genau deshalb wirkte es wie "speichert nicht", nicht wie der 409 von vorhin. PI-
   * Nummer hat keine JTL-Entsprechung und war von diesem zweiten Fehler nie betroffen, deshalb
   * funktionierte laut Ruecklauf nur dieses Feld.
   *
   * Fix: derselbe generische Kopf-Teilupdate-Weg wie beim Bezugsauftrag oben (`onJtlKopfAendern`,
   * `PATCH /api/jtl/orders/:k`) - `cFremdbelegnummer` ist bereits Parameter von
   * `spLieferantenBestellungBearbeiten` (`backend/src/jtl/schreiben/prozeduren.ts:135`) und in
   * `alsTeilKopf` (`jtlSchreiben.ts`) NICHT gesperrt (anders als `kLager`/`cDruckAnmerkung`) - das
   * Backend brauchte dafuer keine einzige neue Zeile.
   */
  const [invoiceNoEntwurf, setInvoiceNoEntwurf] = useState<string>((o.invoiceNo as string) ?? "");
  const vorigeInvoiceNoIdRef = useRef(id);
  const zuletztServerInvoiceNoRef = useRef<string>((o.invoiceNo as string) ?? "");
  useEffect(() => {
    const serverWert = (o.invoiceNo as string) ?? "";
    const bestellungsWechsel = vorigeInvoiceNoIdRef.current !== id;
    const bekannterServerWert = zuletztServerInvoiceNoRef.current;
    vorigeInvoiceNoIdRef.current = id;
    zuletztServerInvoiceNoRef.current = serverWert;
    if (bestellungsWechsel) {
      setInvoiceNoEntwurf(serverWert);
      return;
    }
    setInvoiceNoEntwurf((bisher) => (bisher === bekannterServerWert ? serverWert : bisher));
  }, [id, o.invoiceNo]);

  /**
   * Siehe Kopfkommentar `invoiceNoEntwurf` oben - schreibt nur bei Abweichung, aber ueber
   * `onJtlKopfAendern` (`cFremdbelegnummer`), NICHT mehr `patch(...)`/`onAendern`.
   */
  const invoiceNoVerlassen = (): void => {
    const bisher = (o.invoiceNo as string) ?? "";
    if (invoiceNoEntwurf === bisher) return;
    onJtlKopfAendern?.(id, { cFremdbelegnummer: invoiceNoEntwurf });
    // Fachregel: eine eingetragene Rechnungsnummer hebt "Stock Order" auf (nur die Richtung Nummer -> Haekchen;
    // das Haekchen bei vorhandener Nummer zu setzen bleibt erlaubt).
    if (invoiceNoEntwurf.trim() !== "" && o.stockOrder) patch("stockOrder", false);
  };

  return (
    <>
      <div
        className={["field wide", feldZustandClass({ gesperrt: true })].filter(Boolean).join(" ")}
        title="Lieferant wird bei der Anlage festgelegt und ist danach nicht mehr änderbar."
      >
        <label>Lieferant</label>
        <span className="feld-lock-anchor">
          <span className="feld-lock-icon" aria-hidden="true" data-feld-lock="true">🔒</span>
        </span>
        {o.supplier ? (
          <div className="supplier-preview">
            <LieferantenLogo name={o.supplier} lieferanten={lieferanten} />
            <div>
              <div className="sp-name">{o.supplier as string}</div>
              <div className="sp-note">Ausgewählter Lieferant</div>
            </div>
          </div>
        ) : null}
        {/*
         * Read-only seit Auftrag "Bestellfelder nach Anlage sperren" (2026-08-25): der Lieferant
         * wird bei der Anlage festgelegt (Anlagemaske) und ist danach unveraenderlich. Die Auswahl
         * (`supplierSelectOptions`, index.html:5017) entfaellt damit ersatzlos. `supplierNames`
         * bleibt exportiert - Task-5a-Riegel `test/komponenten/api/bestellungen.test.ts` ruft sie
         * weiterhin direkt auf.
         *
         * KEIN eigenes Eingabefeld mehr (Nachtrag 2, 2026-08-25): ein zusaetzliches
         * `<input className="mono" readOnly>` zeigte den Namen ein zweites Mal an, direkt neben
         * der `.sp-name`-Anzeige oben - dieselbe Redundanz, die die Trackingfeld-Bereinigung vom
         * selben Tag (`ShipTab/Referenzen.tsx`) ersatzlos entfernt statt sie zusaetzlich read-only
         * zu stellen. Die kleine Logo-Vorschau bleibt als Platzhalter fuer den (praktisch nie
         * eintretenden) Fall eines leeren `o.supplier` stehen.
         *
         * G-1 (Nachtrag 3, 2026-08-25): `bestellungen` und `onLieferant` waren bis dahin noch
         * Props dieser Komponente, ohne dass eine Zeile hier sie las - beide Auswahllisten, die sie
         * einst brauchten (Lieferanten-`<select>` oben, Lager-`<select>` unten), sind seit den
         * beiden vorigen Absaetzen entfernt. Vollstaendig gestrichen (Props, Typen, Aufrufstellen in
         * `BestellungTab.tsx`/`MainTab/typen.ts`/`App.tsx`), nicht nur hier stillgelegt.
         */}
        <div className="supplier-select-row">
          <LieferantenLogo
            name={(o.supplier as string) || "Lieferant"}
            lieferanten={lieferanten}
            className="supplier-logo sm"
          />
        </div>
      </div>
      <div className="field wide">
        <label>Lager</label>
        {/*
         * Read-only seit Nachtrag "Bestellfelder nach Anlage sperren", Abschnitt 5 (2026-08-25):
         * `order_extras.warehouse` existiert seit Migration 017 (2026-08-22) nicht mehr, das
         * Ziellager kommt seither ausschliesslich aus JTL (`kLager`) und wird bei der Anlage
         * festgelegt - danach ist es unveraenderlich (Backend-Riegel in
         * `http/routes/jtlSchreiben.ts#alsTeilKopf`, weist `kopf.kLager` mit 400 ab). Dieselbe
         * Behandlung wie das Lieferanten-Feld oben: die Auswahl (`warehouseSelectOptions`,
         * index.html:5029) entfaellt ersatzlos, `lagerAuswahl`/`lagerKennung` haben hier keinen
         * Aufrufer mehr (bleiben exportiert - `Anlagemaske.tsx` braucht sie fuer die NEUANLAGE
         * weiterhin).
         */}
        {/*
         * Auftrag 8 (strukturell statt Sibling-Element): kein `.feld-lock-anchor`-Span mehr - das
         * Schloss-Symbol kommt seither als CSS `background-image` direkt ueber `.feld-gesperrt`
         * (`styles/neubau/feldzustand.css`).
         */}
        <input
          className={feldZustandClass({ gesperrt: true })}
          value={(o.lagerName as string) ?? ""}
          readOnly
          aria-readonly="true"
          title="Lagerwert kommt aus JTL und ist nach Anlage nicht änderbar."
        />
      </div>
      <div className="field">
        {/*
         * Root-Cause-Nachbesserung (Task 1218081223201411, 2026-09-04): gezeichnet wird der LOKALE
         * Entwurf (`piNoEntwurf`), `onInput` haelt ihn waehrend des Tippens nach, geschrieben wird
         * erst bei `onBlur` (`piNoVerlassen`, Kopfkommentar oben an der Komponente).
         */}
        <label>PI-Nummer</label>
        <input
          className={["mono", feldZustandClass({ erwartet: true, leer: !piNoEntwurf })]
            .filter(Boolean)
            .join(" ")}
          value={piNoEntwurf}
          placeholder="PI-…"
          onInput={(e) => setPiNoEntwurf(e.currentTarget.value)}
          onChange={() => {}}
          onBlur={piNoVerlassen}
        />
      </div>
      <div className="field">
        {/*
         * Entwurf/onBlur wie beim PI-Nummer-Feld oben, aber ueber `onJtlKopfAendern` geschrieben
         * (`invoiceNoVerlassen`, Kopfkommentar an `invoiceNoEntwurf` oben) - `invoiceNo` ist die
         * JTL-Fremdbelegnummer, keine reine Hub-Spalte wie `piNo`.
         */}
        <label>Rechnungsnummer</label>
        <input
          className={["mono", feldZustandClass({ gesperrt: sammelsendungGesperrt })].filter(Boolean).join(" ")}
          value={invoiceNoEntwurf}
          disabled={sammelsendungGesperrt}
          title={sammelsendungGesperrt ? SAMMELSENDUNG_PFLEGEHINWEIS : undefined}
          placeholder="RE-…"
          onInput={(e) => setInvoiceNoEntwurf(e.currentTarget.value)}
          onChange={() => {}}
          onBlur={invoiceNoVerlassen}
        />
      </div>
      <div className="field">
        {/*
         * „Bezugsauftrag" (Auftrag „JTL-Felder ins Frontend", 2026-08-21; schreibbar seit Auftrag
         * „Bezugsauftrag schreibbar machen ueber den generischen Weg", 2026-08-25) -
         * `bezugsAuftragsNummer` kommt weiterhin ausschliesslich aus JTL (`cBezugsAuftragsNummer`,
         * siehe Kopfkommentar von `api/jtlBestellungen.ts`), geht seither aber ueber denselben
         * generischen Kopf-Teilupdate-Weg zurueck (`onJtlKopfAendern`, `PATCH /api/jtl/orders/:k`)
         * wie Lager (oben) und interner Kommentar (`BestellungTab.tsx`) - kein `readOnly` mehr.
         * Gezeichnet wird der LOKALE Entwurf (`bezugsauftragEntwurf`), `onInput` haelt ihn waehrend
         * des Tippens nach, geschrieben wird erst bei `onBlur` (`bezugsauftragVerlassen`,
         * Kopfkommentar oben an der Komponente).
         */}
        <label>Bezugsauftrag</label>
        <input
          className="mono"
          value={bezugsauftragEntwurf}
          onInput={(e) => setBezugsauftragEntwurf(e.currentTarget.value)}
          onChange={() => {}}
          onBlur={bezugsauftragVerlassen}
        />
      </div>
      <div className="field">
        {/*
         * „Stock Order" (Auftrag Liquiditaetsplanung): Lagerbestellung ja/nein, `order_extras.stock_order`.
         * Schreibt sofort wie die uebrigen order_extras-Felder ueber `patch(...)` -> `onAendern`.
         */}
        <label className="haken-feld">
          <input
            type="checkbox"
            checked={!!o.stockOrder}
            onChange={(e) => patch("stockOrder", e.currentTarget.checked)}
          />
          Stock Order
        </label>
      </div>
      <div className="field wide">
        <label>Liefertermin (Text)</label>
        {/*
         * Read-only seit Auftrag "Liefertermin auf echtes JTL-Feld umstellen" (2026-08-26): dieses
         * Freitextfeld unterhalb der Artikelpositionen im JTL-Beschaffungsfenster (`cDruckAnmerkung`)
         * wird bei der Anlage festgelegt (`Anlagemaske.tsx#liefertermin` -> `deliveryTermText` in
         * `POST /api/jtl/orders`) und ist danach unveraenderlich - dieselbe Behandlung wie Lieferant
         * und Lager oben (Backend-Riegel `http/routes/jtlSchreiben.ts#alsTeilKopf`, weist
         * `kopf.cDruckAnmerkung` mit 400 ab).
         *
         * ERSETZT den kompletten "einmal befuellbar, danach gesperrt"-Apparat aus den zwei vorigen
         * Nachtraegen zu "Bestellfelder nach Anlage sperren" (lokaler Entwurfszustand `terminEntwurf`,
         * eigene Fehlerklasse `DeliveryTermTextBereitsGesetztError`, Hub-Spalte
         * `order_extras.delivery_term_text`) - der galt nur, solange JTLs Anlage-Prozedur keine
         * Entsprechung fuer diesen Freitext kannte. Seit diesem Auftrag ist `cDruckAnmerkung` bereits
         * beim Anlegen Parameter von `spLieferantenBestellungErstellen`, die Anlagemaske verwirft den
         * eingegebenen Wert nicht mehr - eine unbedingte Sperre ist damit richtig, nicht mehr falsch.
         */}
        {/* Auftrag 8: kein `.feld-lock-anchor`-Span mehr, siehe Kommentar am Lager-Feld oben. */}
        <input
          className={feldZustandClass({ gesperrt: true })}
          value={(o.deliveryTermText as string) ?? ""}
          readOnly
          aria-readonly="true"
          title="Freitext aus dem JTL-Beschaffungsfenster, hier nicht änderbar."
        />
      </div>
    </>
  );
}
